/**
 * --------------------------------------------------------------------------
 * Bootstrap (v4.6.2): dropdown.js
 * Licensed under MIT (https://github.com/twbs/bootstrap/blob/main/LICENSE)
 * --------------------------------------------------------------------------
 */

import Popper from 'popper.js'
import Util from '../BootstrapUtil/bootstrap-util'

/**
 * Constants
 */

const NAME = 'dropdown'
const VERSION = '4.6.2'
const DATA_KEY = 'bs.dropdown'
const EVENT_KEY = `.${DATA_KEY}`
const DATA_API_KEY = '.data-api'
const ESCAPE_KEYCODE = 27 // KeyboardEvent.which value for Escape (Esc) key
const SPACE_KEYCODE = 32 // KeyboardEvent.which value for space key
const TAB_KEYCODE = 9 // KeyboardEvent.which value for tab key
const ARROW_UP_KEYCODE = 38 // KeyboardEvent.which value for up arrow key
const ARROW_DOWN_KEYCODE = 40 // KeyboardEvent.which value for down arrow key
const RIGHT_MOUSE_BUTTON_WHICH = 3 // MouseEvent.which value for the right button (assuming a right-handed mouse)
const REGEXP_KEYDOWN = new RegExp(`${ARROW_UP_KEYCODE}|${ARROW_DOWN_KEYCODE}|${ESCAPE_KEYCODE}`)

const CLASS_NAME_DISABLED = 'disabled'
const CLASS_NAME_SHOW = 'show'
const CLASS_NAME_DROPUP = 'dropup'
const CLASS_NAME_DROPRIGHT = 'dropright'
const CLASS_NAME_DROPLEFT = 'dropleft'
const CLASS_NAME_MENURIGHT = 'dropdown-menu-right'
const CLASS_NAME_POSITION_STATIC = 'position-static'

const EVENT_HIDE = `hide${EVENT_KEY}`
const EVENT_HIDDEN = `hidden${EVENT_KEY}`
const EVENT_SHOW = `show${EVENT_KEY}`
const EVENT_SHOWN = `shown${EVENT_KEY}`
const EVENT_CLICK = `click${EVENT_KEY}`
const EVENT_CLICK_DATA_API = `click${EVENT_KEY}${DATA_API_KEY}`
const EVENT_KEYDOWN_DATA_API = `keydown${EVENT_KEY}${DATA_API_KEY}`
const EVENT_KEYUP_DATA_API = `keyup${EVENT_KEY}${DATA_API_KEY}`

const SELECTOR_DATA_TOGGLE = '[data-toggle="dropdown"]'
const SELECTOR_FORM_CHILD = '.dropdown form'
const SELECTOR_MENU = '.dropdown-menu'
const SELECTOR_NAVBAR_NAV = '.navbar-nav'
const SELECTOR_VISIBLE_ITEMS = '.dropdown-menu .dropdown-item:not(.disabled):not(:disabled)'

const PLACEMENT_TOP = 'top-start'
const PLACEMENT_TOPEND = 'top-end'
const PLACEMENT_BOTTOM = 'bottom-start'
const PLACEMENT_BOTTOMEND = 'bottom-end'
const PLACEMENT_RIGHT = 'right-start'
const PLACEMENT_LEFT = 'left-start'

const Default = {
  offset: 0,
  flip: true,
  boundary: 'scrollParent',
  reference: 'toggle',
  display: 'dynamic',
  popperConfig: null
}

const DefaultType = {
  offset: '(number|string|function)',
  flip: 'boolean',
  boundary: '(string|element)',
  reference: '(string|element)',
  display: 'string',
  popperConfig: '(null|object)'
}

/**
 * Class definition
 */

class Dropdown {
  static _instances = new WeakMap()
  static _touchMouseOverListeners = new Map()

  constructor(element, config) {
    this._element = element
    this._popper = null
    this._config = this._getConfig(config)
    this._menu = this._getMenuElement()
    this._inNavbar = this._detectNavbar()
    this._clickHandler = null

    this._addEventListeners()
    Dropdown._instances.set(this._element, this)
  }

  // Getters
  static get VERSION() {
    return VERSION
  }

  static get Default() {
    return Default
  }

  static get DefaultType() {
    return DefaultType
  }

  // Public
  toggle() {
    if (this._element.disabled || this._element.classList.contains(CLASS_NAME_DISABLED)) {
      return
    }

    const isActive = this._menu.classList.contains(CLASS_NAME_SHOW)

    Dropdown._clearMenus()

    if (isActive) {
      return
    }

    this.show(true)
  }

  show(usePopper = false) {
    if (this._element.disabled || this._element.classList.contains(CLASS_NAME_DISABLED) || this._menu.classList.contains(CLASS_NAME_SHOW)) {
      return
    }

    const relatedTarget = {
      relatedTarget: this._element
    }
    const parent = Dropdown._getParentFromElement(this._element)
    const showEvent = Dropdown._triggerEvent(parent, EVENT_SHOW, relatedTarget)

    if (showEvent.defaultPrevented) {
      return
    }

    // Totally disable Popper for Dropdowns in Navbar
    if (!this._inNavbar && usePopper) {
      // Check for Popper dependency
      if (typeof Popper === 'undefined') {
        throw new TypeError('Bootstrap\'s dropdowns require Popper (https://popper.js.org)')
      }

      let referenceElement = this._element

      if (this._config.reference === 'parent') {
        referenceElement = parent
      } else if (Util.isElement(this._config.reference)) {
        referenceElement = this._config.reference

        // Check if it's jQuery element
        if (typeof this._config.reference.jquery !== 'undefined') {
          referenceElement = this._config.reference[0]
        }
      }

      // If boundary is not `scrollParent`, then set position to `static`
      // to allow the menu to "escape" the scroll parent's boundaries
      // https://github.com/twbs/bootstrap/issues/24251
      if (this._config.boundary !== 'scrollParent') {
        parent.classList.add(CLASS_NAME_POSITION_STATIC)
      }

      this._popper = new Popper(referenceElement, this._menu, this._getPopperConfig())
    }

    // If this is a touch-enabled device we add extra
    // empty mouseover listeners to the body's immediate children;
    // only needed because of broken event delegation on iOS
    // https://www.quirksmode.org/blog/archives/2014/02/mouse_event_bub.html
    if ('ontouchstart' in document.documentElement &&
        parent.closest(SELECTOR_NAVBAR_NAV) === null) {
      Dropdown._addTouchListeners()
    }

    this._element.focus()
    this._element.setAttribute('aria-expanded', true)

    this._menu.classList.toggle(CLASS_NAME_SHOW)
    parent.classList.toggle(CLASS_NAME_SHOW)
    Dropdown._triggerEvent(parent, EVENT_SHOWN, relatedTarget)
  }

  hide() {
    if (this._element.disabled || this._element.classList.contains(CLASS_NAME_DISABLED) || !this._menu.classList.contains(CLASS_NAME_SHOW)) {
      return
    }

    const relatedTarget = {
      relatedTarget: this._element
    }
    const parent = Dropdown._getParentFromElement(this._element)
    const hideEvent = Dropdown._triggerEvent(parent, EVENT_HIDE, relatedTarget)
    if (hideEvent.defaultPrevented) {
      return
    }

    if (this._popper) {
      this._popper.destroy()
    }

    this._menu.classList.toggle(CLASS_NAME_SHOW)
    parent.classList.toggle(CLASS_NAME_SHOW)
    Dropdown._triggerEvent(parent, EVENT_HIDDEN, relatedTarget)
  }

  dispose() {
    Dropdown._instances.delete(this._element)
    if (this._clickHandler) {
      this._element.removeEventListener('click', this._clickHandler)
      this._clickHandler = null
    }

    this._element = null
    this._menu = null
    if (this._popper !== null) {
      this._popper.destroy()
      this._popper = null
    }
  }

  update() {
    this._inNavbar = this._detectNavbar()
    if (this._popper !== null) {
      this._popper.scheduleUpdate()
    }
  }

  // Private
  _addEventListeners() {
    this._clickHandler = event => {
      event.preventDefault()
      event.stopPropagation()
      this.toggle()
    }
    this._element.addEventListener('click', this._clickHandler)
  }

  _getConfig(config) {
    config = {
      ...this.constructor.Default,
      ...Dropdown._getDataAttributes(this._element),
      ...config
    }

    Util.typeCheckConfig(
      NAME,
      config,
      this.constructor.DefaultType
    )

    return config
  }

  _getMenuElement() {
    if (!this._menu) {
      const parent = Dropdown._getParentFromElement(this._element)

      if (parent) {
        this._menu = parent.querySelector(SELECTOR_MENU)
      }
    }

    return this._menu
  }

  _getPlacement() {
    const parentDropdown = this._element.parentNode
    let placement = PLACEMENT_BOTTOM

    // Handle dropup
    if (parentDropdown.classList.contains(CLASS_NAME_DROPUP)) {
      placement = this._menu.classList.contains(CLASS_NAME_MENURIGHT) ?
        PLACEMENT_TOPEND :
        PLACEMENT_TOP
    } else if (parentDropdown.classList.contains(CLASS_NAME_DROPRIGHT)) {
      placement = PLACEMENT_RIGHT
    } else if (parentDropdown.classList.contains(CLASS_NAME_DROPLEFT)) {
      placement = PLACEMENT_LEFT
    } else if (this._menu.classList.contains(CLASS_NAME_MENURIGHT)) {
      placement = PLACEMENT_BOTTOMEND
    }

    return placement
  }

  _detectNavbar() {
    return this._element.closest('.navbar') !== null
  }

  _getOffset() {
    const offset = {}

    if (typeof this._config.offset === 'function') {
      offset.fn = data => {
        data.offsets = {
          ...data.offsets,
          ...this._config.offset(data.offsets, this._element)
        }

        return data
      }
    } else {
      offset.offset = this._config.offset
    }

    return offset
  }

  _getPopperConfig() {
    const popperConfig = {
      placement: this._getPlacement(),
      modifiers: {
        offset: this._getOffset(),
        flip: {
          enabled: this._config.flip
        },
        preventOverflow: {
          boundariesElement: this._config.boundary
        }
      }
    }

    // Disable Popper if we have a static display
    if (this._config.display === 'static') {
      popperConfig.modifiers.applyStyle = {
        enabled: false
      }
    }

    return {
      ...popperConfig,
      ...this._config.popperConfig
    }
  }

  // Static
  static getInstance(element) {
    return Dropdown._instances.get(element) || null
  }

  static getOrCreateInstance(element, config = {}) {
    return this.getInstance(element) || new this(element, config)
  }

  static _getDataAttributes(element) {
    if (!element) {
      return {}
    }

    const parseValue = value => {
      if (value === 'true') {
        return true
      }
      if (value === 'false') {
        return false
      }
      if (value === '' || value === null || typeof value === 'undefined') {
        return value
      }
      if (!Number.isNaN(Number(value)) && value.trim() !== '') {
        return Number(value)
      }
      try {
        return JSON.parse(value)
      } catch (_) {
        return value
      }
    }

    return {
      offset: parseValue(element.dataset.offset),
      flip: parseValue(element.dataset.flip),
      boundary: parseValue(element.dataset.boundary),
      reference: parseValue(element.dataset.reference),
      display: parseValue(element.dataset.display),
      popperConfig: parseValue(element.dataset.popperConfig)
    }
  }

  static interface(elements, config) {
    elements.forEach(element => {
      let data = Dropdown.getInstance(element)
      const _config = typeof config === 'object' ? config : null

      if (!data) {
        data = new Dropdown(element, _config)
      }

      if (typeof config === 'string') {
        if (typeof data[config] === 'undefined') {
          throw new TypeError(`No method named "${config}"`)
        }
        data[config]()
      }
    })
  }

  static _triggerEvent(element, eventName, detail = {}) {
    const event = new CustomEvent(eventName, {
      bubbles: true,
      cancelable: true,
      detail
    })

    if (typeof detail.relatedTarget !== 'undefined') {
      Object.defineProperty(event, 'relatedTarget', {
        configurable: true,
        enumerable: true,
        value: detail.relatedTarget
      })
    }

    if (typeof detail.clickEvent !== 'undefined') {
      Object.defineProperty(event, 'clickEvent', {
        configurable: true,
        enumerable: true,
        value: detail.clickEvent
      })
    }

    element.dispatchEvent(event)

    const jQueryRef = window.jQuery
    if (typeof jQueryRef !== 'undefined') {
      const jqEvent = jQueryRef.Event(eventName, detail)
      jQueryRef(element).trigger(jqEvent)
      if (jqEvent.isDefaultPrevented()) {
        event.preventDefault()
      }
    }

    return event
  }

  static _addTouchListeners() {
    const children = Array.from(document.body.children)
    children.forEach(child => {
      if (Dropdown._touchMouseOverListeners.has(child)) {
        return
      }
      const noop = () => {}
      child.addEventListener('mouseover', noop)
      Dropdown._touchMouseOverListeners.set(child, noop)
    })
  }

  static _removeTouchListeners() {
    Dropdown._touchMouseOverListeners.forEach((handler, element) => {
      element.removeEventListener('mouseover', handler)
    })
    Dropdown._touchMouseOverListeners.clear()
  }

  static _clearMenus(event) {
    const which = event ? (event.which || event.keyCode) : null
    const isRightClick = event && (which === RIGHT_MOUSE_BUTTON_WHICH || event.button === 2)
    if (event && (isRightClick ||
      event.type === 'keyup' && which !== TAB_KEYCODE)) {
      return
    }

    const toggles = [].slice.call(document.querySelectorAll(SELECTOR_DATA_TOGGLE))

    for (let i = 0, len = toggles.length; i < len; i++) {
      const parent = Dropdown._getParentFromElement(toggles[i])
      const context = Dropdown.getInstance(toggles[i])
      const relatedTarget = {
        relatedTarget: toggles[i]
      }

      if (event && event.type === 'click') {
        relatedTarget.clickEvent = event
      }

      if (!context || !parent) {
        continue
      }

      const dropdownMenu = context._menu
      if (!parent.classList.contains(CLASS_NAME_SHOW)) {
        continue
      }

      if (
        event &&
        (
          (event.type === 'click' && /input|textarea/i.test(event.target.tagName)) ||
          (event.type === 'keyup' && which === TAB_KEYCODE)
        ) &&
        parent.contains(event.target)
      ) {
        continue
      }

      const hideEvent = Dropdown._triggerEvent(parent, EVENT_HIDE, relatedTarget)
      if (hideEvent.defaultPrevented) {
        continue
      }

      // If this is a touch-enabled device we remove the extra
      // empty mouseover listeners we added for iOS support
      if ('ontouchstart' in document.documentElement) {
        Dropdown._removeTouchListeners()
      }

      toggles[i].setAttribute('aria-expanded', 'false')

      if (context._popper) {
        context._popper.destroy()
      }

      dropdownMenu.classList.remove(CLASS_NAME_SHOW)
      parent.classList.remove(CLASS_NAME_SHOW)
      Dropdown._triggerEvent(parent, EVENT_HIDDEN, relatedTarget)
    }
  }

  static _getParentFromElement(element) {
    let parent
    const selector = Util.getSelectorFromElement(element)

    if (selector) {
      parent = document.querySelector(selector)
    }

    return parent || element.parentNode
  }

  // eslint-disable-next-line complexity
  static _dataApiKeydownHandler(event) {
    const which = event.which || event.keyCode
    // If not input/textarea:
    //  - And not a key in REGEXP_KEYDOWN => not a dropdown command
    // If input/textarea:
    //  - If space key => not a dropdown command
    //  - If key is other than escape
    //    - If key is not up or down => not a dropdown command
    //    - If trigger inside the menu => not a dropdown command
    if (/input|textarea/i.test(event.target.tagName) ?
      which === SPACE_KEYCODE || which !== ESCAPE_KEYCODE &&
      (which !== ARROW_DOWN_KEYCODE && which !== ARROW_UP_KEYCODE ||
        event.target.closest(SELECTOR_MENU)) : !REGEXP_KEYDOWN.test(which)) {
      return
    }

    if (this.disabled || this.classList.contains(CLASS_NAME_DISABLED)) {
      return
    }

    const parent = Dropdown._getParentFromElement(this)
    const isActive = parent.classList.contains(CLASS_NAME_SHOW)

    if (!isActive && which === ESCAPE_KEYCODE) {
      return
    }

    event.preventDefault()
    event.stopPropagation()

    if (!isActive || (which === ESCAPE_KEYCODE || which === SPACE_KEYCODE)) {
      if (which === ESCAPE_KEYCODE) {
        parent.querySelector(SELECTOR_DATA_TOGGLE)?.focus()
      }

      this.click()
      return
    }

    const items = [].slice.call(parent.querySelectorAll(SELECTOR_VISIBLE_ITEMS))
      .filter(item => item.offsetParent !== null || item.getClientRects().length > 0)

    if (items.length === 0) {
      return
    }

    let index = items.indexOf(event.target)

    if (which === ARROW_UP_KEYCODE && index > 0) { // Up
      index--
    }

    if (which === ARROW_DOWN_KEYCODE && index < items.length - 1) { // Down
      index++
    }

    if (index < 0) {
      index = 0
    }

    items[index].focus()
  }
}

/**
 * Data API implementation
 */

document.addEventListener('keydown', event => {
  const trigger = event.target.closest(SELECTOR_DATA_TOGGLE)
  if (trigger) {
    Dropdown._dataApiKeydownHandler.call(trigger, event)
    return
  }

  const menu = event.target.closest(SELECTOR_MENU)
  if (menu) {
    Dropdown._dataApiKeydownHandler.call(menu, event)
  }
})

document.addEventListener('keyup', event => {
  Dropdown._clearMenus(event)
})

document.addEventListener('click', event => {
  const formChild = event.target.closest(SELECTOR_FORM_CHILD)
  if (formChild) {
    event.stopPropagation()
    return
  }

  const toggle = event.target.closest(SELECTOR_DATA_TOGGLE)
  if (toggle) {
    event.preventDefault()
    event.stopPropagation()
    Dropdown.interface([toggle], 'toggle')
    return
  }

  Dropdown._clearMenus(event)
})

/**
 * Optional jQuery bridge
 */
if (typeof window.jQuery !== 'undefined') {
  const $ = window.jQuery
  const JQUERY_NO_CONFLICT = $.fn[NAME]
  $.fn[NAME] = function (config) {
    return Dropdown.interface([].slice.call(this), config)
  }
  $.fn[NAME].Constructor = Dropdown
  $.fn[NAME].noConflict = () => {
    $.fn[NAME] = JQUERY_NO_CONFLICT
    return $.fn[NAME]
  }
}

export default Dropdown
